import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { showSuccess, showWarning, showInfo } from '../../utils/toastUtils';
import { toast } from 'react-toastify';
import currencyConfig from '../../utils/currencyConfig';
import { dispatchCartEvent, CART_EVENTS } from '../../utils/cartEvents';
import { formatDate } from '../../utils/dateUtils';
import { offeringsAPI } from '../../services/api';

// Simple Home icon for breadcrumb (kept lightweight)
const HomeIcon = () => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ cursor: "pointer" }}
    onClick={() => window.location.href = "/home"}
  >
    <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
  </svg>
);

// Styles ONLY for content area (Layout supplies container/header/sidebar)
const baseStyles = {
  breadcrumbContent: { display: 'flex', alignItems: 'center', gap: '8px' },
  checkoutContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    maxWidth: '1100px',
    width: '100%',
    minHeight: '600px',
    margin: '40px auto 40px',
  },
  stepContainer: { padding: '30px' },
  stepTitle: { fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', color: '#333' },
  stepSubtitle: { fontSize: '16px', color: '#666', marginBottom: '30px' },
  formContainer: { display: 'flex', gap: '40px', flexWrap: 'wrap' },
  leftColumn: { flex: 1, minWidth: '260px' },
  rightColumn: { flex: 1, minWidth: '260px' },
  formGroup: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' },
  input: { width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' },
  orderSummary: { backgroundColor: '#ffffff', padding: '0', borderRadius: '0', border: 'none', width: '100%' },
  orderSummaryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', borderBottom: '1px solid #ddd', marginBottom: '20px' },
  orderSummaryHeaderItems: { fontSize: '16px', fontWeight: 600, color: '#333' },
  orderSummaryHeaderTotal: { fontSize: '16px', fontWeight: 600, color: '#333' },
  orderSummaryContent: { display: 'flex', flexDirection: 'column' },
  orderSummaryItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' },
  orderSummaryItemName: { fontSize: '14px', color: '#666', flex: 1 },
  orderSummaryItemPrice: { fontSize: '14px', fontWeight: 500, color: '#333' },
  orderSummaryTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', marginTop: '20px', borderTop: '1px solid #ddd' },
  orderSummaryTotalLabel: { fontSize: '16px', fontWeight: 600, color: '#333' },
  orderSummaryTotalPrice: { fontSize: '16px', fontWeight: 700, color: '#333' },
  nextButton: { width: '200px', padding: '8px 16px', backgroundColor: '#000', color: 'white', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 500, cursor: 'pointer', marginTop: '20px', transition: 'background-color 0.2s' },
  paymentMethodContainer: { display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '10px', padding: '40px 0' },
  paymentMethod: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px', border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#f8f9fa', minWidth: '200px', transition: 'all 0.3s ease' },
  paymentMethodDisabled: { backgroundColor: '#e9ecef', cursor: 'not-allowed', opacity: 0.7 },
  paymentMethodIcon: { fontSize: '48px', marginBottom: '20px' },
  paymentMethodLabel: { fontSize: '16px', fontWeight: 'bold', color: '#007bff' },
  unavailableText: { fontSize: '14px', fontWeight: 'bold', color: '#6c757d', textAlign: 'center', marginBottom: '8px' },
  itemsList: { marginBottom: '20px' },
  itemRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderTop: '2px solid #333', marginTop: '10px' },
  confirmButton: { backgroundColor: '#7fb3a3', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', width: '100%' },
  detailsDisplay: { marginBottom: '30px' },
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderBottom: '1px solid #eee' },
  detailLabel: { fontWeight: 'bold', color: '#333' },
  detailValue: { color: '#666' },
  successMessage: { textAlign: 'center', marginBottom: '40px' },
  referenceNumber: { fontSize: '24px', fontWeight: 'bold', color: '#333', margin: '20px 0' },
  note: { color: '#dc3545', fontSize: '14px', fontWeight: 'bold', marginTop: '20px' },
  summaryTitle: { fontSize: '24px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' },
  summaryContainer: { display: 'flex', gap: '40px', flexWrap: 'wrap' },
  summaryLeft: { flex: 1, minWidth: '260px' },
  summaryRight: { flex: 1, minWidth: '260px' },
  summaryItemRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', border: '1px solid #ddd', paddingLeft: '10px', paddingRight: '10px', marginBottom: '4px' },
  summaryTotalRow: { display: 'flex', justifyContent: 'space-between', padding: '15px 10px', border: '2px solid #333', marginTop: '10px' },
  summaryDetailRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0' },
  summaryLabel: { fontWeight: 'bold', color: '#333' },
  summaryValue: { color: '#666' },
};

// STEP COMPONENTS
const CustomerDetailsStep = ({ styles, customerDetails, handleInputChange, basketItems, calculateTotal, handleCustomerDetailsSubmit }) => (
  <div style={styles.stepContainer}>
    <h2 style={styles.stepTitle}>Your Details</h2>
    <p style={styles.stepSubtitle}>Please enter required information</p>
    <div style={styles.formContainer}>
      <div style={styles.leftColumn}>
        {['fullName','email','confirmEmail','division','lineage'].map((field, idx) => (
          <div style={styles.formGroup} key={field}>
            <label style={styles.label}>{
              field === 'fullName' ? 'Full Name*' :
              field === 'email' ? 'Email Address*' :
              field === 'confirmEmail' ? 'Confirm Email Address*' :
              field.charAt(0).toUpperCase() + field.slice(1)
            }</label>
            <input
              type={field.toLowerCase().includes('email') ? 'email' : 'text'}
              value={customerDetails[field]}
              onChange={handleInputChange(field)}
              style={styles.input}
              required={field === 'fullName' || field === 'email' || field === 'confirmEmail'}
              onPaste={(e) => {
                if (field === 'confirmEmail') {
                  e.preventDefault();
                  showWarning('Paste is not allowed in confirm email field. Please type it manually.');
                }
              }}
            />
          </div>
        ))}
      </div>
      <div style={styles.rightColumn}>
        <div style={styles.orderSummary}>
          <div style={styles.orderSummaryHeader}>
            <span style={styles.orderSummaryHeaderItems}>Items</span>
            <span style={styles.orderSummaryHeaderTotal}>Total</span>
          </div>
          <div style={styles.orderSummaryContent}>
            {basketItems.map((item, index) => (
              <div key={index} style={styles.orderSummaryItem}>
                <span style={styles.orderSummaryItemName}>{item.offering_type_name || item.title || 'Homa Regular Request'}</span>
                <span style={styles.orderSummaryItemPrice}>{currencyConfig.format(item.amount)}</span>
              </div>
            ))}
            <div style={styles.orderSummaryTotal}>
              <span style={styles.orderSummaryTotalLabel}>Total</span>
              <span style={styles.orderSummaryTotalPrice}>{currencyConfig.format(calculateTotal())}</span>
            </div>
          </div>
          <button style={styles.nextButton} onClick={handleCustomerDetailsSubmit}>Next</button>
        </div>
      </div>
    </div>
  </div>
);

const PaymentMethodStep = ({ styles, handlePaymentMethodSelection }) => (
  <div style={styles.stepContainer}>
    <h2 style={styles.stepTitle}>Select Payment Method</h2>
    <div style={styles.paymentMethodContainer}>
      <div style={{ ...styles.paymentMethod, ...styles.paymentMethodDisabled }} onClick={() => showWarning('Card payment is currently unavailable')}>
        <div style={styles.paymentMethodIcon}>💳</div>
        <div>
          <div style={styles.unavailableText}>UNAVAILABLE</div>
          <div style={styles.paymentMethodLabel}>CARD PAYMENT</div>
        </div>
      </div>
      <div style={styles.paymentMethod} onClick={() => handlePaymentMethodSelection('bank_transfer')}>
        <div style={styles.paymentMethodIcon}>🏛️</div>
        <div style={styles.paymentMethodLabel}>BANK TRANSFER</div>
      </div>
    </div>
  </div>
);

const PaymentDetailsStep = ({ styles, customerDetails, basketItems, calculateTotal, handleFinalCheckout, loading }) => (
  <div style={styles.stepContainer}>
    <h2 style={styles.stepTitle}>Your Details</h2>
    <div style={styles.formContainer}>
      <div style={styles.leftColumn}>
        <div style={styles.detailsDisplay}>
          {['Full Name','Email Address','Division','Lineage','Payment Method'].map(label => (
            <div style={styles.detailRow} key={label}>
              <span style={styles.detailLabel}>{label}</span>
              <span style={styles.detailValue}>{
                label === 'Full Name' ? customerDetails.fullName :
                label === 'Email Address' ? customerDetails.email :
                label === 'Division' ? customerDetails.division :
                label === 'Lineage' ? customerDetails.lineage :
                'Bank Transfer'
              }</span>
            </div>
          ))}
        </div>
      </div>
      <div style={styles.rightColumn}>
        <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#333' }}>Items</h3>
        <div style={styles.itemsList}>
          {basketItems.map((item, index) => (
            <div key={index} style={styles.itemRow}>
              <span>{item.offering_type_name || 'Item'}</span>
              <span>{currencyConfig.format(item.amount)}</span>
            </div>
          ))}
        </div>
        <div style={styles.totalRow}>
          <strong>Total</strong>
          <strong>{currencyConfig.format(calculateTotal())}</strong>
        </div>
        <button onClick={handleFinalCheckout} style={styles.confirmButton} disabled={loading}>
          {loading ? 'Processing...' : 'CONFIRM PAYMENT'}
        </button>
      </div>
    </div>
  </div>
);

const SummaryStep = ({ styles, orderDetails, basketItems, calculateTotal, customerDetails }) => (
  <div style={styles.stepContainer}>
    <div style={styles.successMessage}>
      <h2>Thank you for making submission. Email confirmation has been sent.</h2>
      <p>Please write your reference number when you make a bank transfer.</p>
      <div style={styles.referenceNumber}>Your Reference Number : {orderDetails?.payment_reference}</div>
      <p style={styles.note}>NOTE : Kindly use the above unique reference number for any Bank Transfers so that it will help us to identify your payment. Thank You.</p>
    </div>
    <h3 style={styles.summaryTitle}>Summary</h3>
    <div style={styles.summaryContainer}>
      <div style={styles.summaryLeft}>
        {/* Itemized list commented out for now */}
        {basketItems.map((item, index) => (
          <div key={index} style={styles.summaryItemRow}>
            <span>{item.offering_type_name || 'Item'} x 1</span>
            <span>{currencyConfig.format(item.amount)}</span>
          </div>
        ))}

        <div style={styles.summaryTotalRow}>
          <strong>Total</strong>
          <strong>{currencyConfig.format(calculateTotal())}</strong>
        </div>
      </div>
      <div style={styles.summaryRight}>
        {['Full Name','Email Address','Division','Lineage','Payment Type','Payment Amount','Reference','Date'].map(label => (
          <div style={styles.summaryDetailRow} key={label}>
            <span style={styles.summaryLabel}>{label}</span>
            <span style={styles.summaryValue}>{
              label === 'Full Name' ? customerDetails.fullName :
              label === 'Email Address' ? customerDetails.email :
              label === 'Division' ? customerDetails.division :
              label === 'Lineage' ? customerDetails.lineage :
              label === 'Payment Type' ? 'Bank Transfer' :
              label === 'Payment Amount' ? currencyConfig.format(calculateTotal()) :
              label === 'Reference' ? (orderDetails?.payment_reference) :
              formatDate(new Date())
            }</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CheckoutFlow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [basketItems, setBasketItems] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({ fullName: '', email: '', confirmEmail: '', division: '', lineage: '' });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const navigate = useNavigate();

  // Fetch basket items
  useEffect(() => {
    const fetchBasketItems = async () => {
      try {
        // Use API service which automatically includes browser_id
        const response = await offeringsAPI.getBasket();
        if (response.data) setBasketItems(response.data || []);
      } catch (err) {
        console.error('Failed to load basket items:', err);
        if (!err.message?.includes('401')) {
          toast.error('Failed to load basket items. Please refresh the page.');
        }
      }
    };
    fetchBasketItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateTotal = useCallback(() => basketItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0), [basketItems]);

  const styles = useMemo(() => baseStyles, []);

  const handleInputChange = useCallback((field) => (e) => {
    setCustomerDetails(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  const handleCustomerDetailsSubmit = () => {
    // Check required fields first
    const requiredFields = ['fullName', 'email', 'confirmEmail'];
    const missingRequiredFields = requiredFields.filter(f => !customerDetails[f]);
    
    if (missingRequiredFields.length > 0) {
      const fieldLabels = {
        fullName: 'Full Name',
        email: 'Email Address',
        confirmEmail: 'Confirm Email Address'
      };
      
      showWarning(`Please fill in all required fields: ${missingRequiredFields.map(f => fieldLabels[f]).join(', ')}`);
      return;
    }
    
    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerDetails.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Check if emails match
    if (customerDetails.email !== customerDetails.confirmEmail) {
      toast.error('Email addresses do not match');
      return;
    }
    
    // Check optional fields
    if (['division','lineage'].some(f => !customerDetails[f])) {
      showWarning('Division and Lineage fields are recommended but not required.');
      // Still allow to proceed
    }
    
    setCurrentStep(2);
  };

  const handlePaymentMethodSelection = (method) => {
    setPaymentMethod(method);
    if (method === 'bank_transfer') {
      setCurrentStep(3);
    } else {
      showWarning('Card payment is currently unavailable. Please select Bank Transfer.');
    }
  };

  const handleFinalCheckout = async () => {
    setLoading(true);
    try {
      // Use API service which automatically includes browser_id
      const checkoutData = { payment_method: paymentMethod, customer_details: customerDetails };
      const response = await offeringsAPI.checkout(checkoutData);
      
      if (response.success) { 
        setOrderDetails(response.data); 
        setCurrentStep(4);
        showSuccess('Checkout completed successfully!');
        
        // Dispatch cart event to notify that checkout is completed
        // This will reset the cart count in the header immediately
        dispatchCartEvent(CART_EVENTS.CHECKOUT_COMPLETED, { orderId: response.data.payment_id });
        dispatchCartEvent(CART_EVENTS.CART_UPDATED, { count: 0 });
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.message || 'Checkout failed. Please try again.');
    } finally { 
      setLoading(false); 
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return <CustomerDetailsStep styles={styles} customerDetails={customerDetails} handleInputChange={handleInputChange} basketItems={basketItems} calculateTotal={calculateTotal} handleCustomerDetailsSubmit={handleCustomerDetailsSubmit} />;
      case 2: return <PaymentMethodStep styles={styles} handlePaymentMethodSelection={handlePaymentMethodSelection} />;
      case 3: return <PaymentDetailsStep styles={styles} customerDetails={customerDetails} basketItems={basketItems} calculateTotal={calculateTotal} handleFinalCheckout={handleFinalCheckout} loading={loading} />;
      case 4: return <SummaryStep styles={styles} orderDetails={orderDetails} basketItems={basketItems} calculateTotal={calculateTotal} customerDetails={customerDetails} />;
      default: return null;
    }
  };

  const breadcrumb = (
    <div style={styles.breadcrumbContent}>
      <HomeIcon />
      <span>/</span>
      <span style={{ cursor: "pointer" }} onClick={() => window.location.href = "/home"}>Home</span>
      <span>/</span>
      <span style={{ cursor: "pointer" }} onClick={() => window.location.href = "/basket"}>Basket</span>
      <span>/</span>
      <span>Checkout</span>
    </div>
  );

  return (
    <Layout activeMenuItem="My Basket" breadcrumb={breadcrumb}>
      <div style={styles.checkoutContainer}>
        {renderCurrentStep()}
      </div>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <button
          style={{ ...styles.nextButton, backgroundColor: '#7fb3a3', width: 'auto' }}
          onClick={() => navigate('/')}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#6ca193'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#7fb3a3'; }}
        >Back to Home</button>
      </div>
      {/* Toast notifications will be shown via react-toastify */}
    </Layout>
  );
};

export default CheckoutFlow;
